import { useEffect, useRef } from 'react';

/** Cross-page HR refresh — employees, departments, and portal pickers reload when linked data changes. */

export const HR_EMPLOYEES_REFRESH_EVENT = 'hr-employees-changed';
export const HR_DEPARTMENTS_REFRESH_EVENT = 'hr-departments-changed';
export const HR_LEAVES_REFRESH_EVENT = 'hr-leaves-changed';
export const HR_ACCIDENTS_REFRESH_EVENT = 'hr-accidents-changed';
export const HR_CONTRACTS_REFRESH_EVENT = 'hr-contracts-changed';
export const HR_TRAINING_REFRESH_EVENT = 'hr-training-changed';
export const HR_EVALUATIONS_REFRESH_EVENT = 'hr-evaluations-changed';
export const HR_PORTAL_DOCUMENTS_REFRESH_EVENT = 'hr-portal-documents-changed';
export const HR_PORTAL_SERVICE_REQUESTS_REFRESH_EVENT = 'hr-portal-service-requests-changed';
export const HR_PORTAL_ANNOUNCEMENTS_REFRESH_EVENT = 'hr-portal-announcements-changed';
export const HR_PORTAL_DASHBOARD_REFRESH_EVENT = 'hr-portal-dashboard-changed';

export const HR_REFRESH_BY_API = {
  '/hr/employees': HR_EMPLOYEES_REFRESH_EVENT,
  '/management/departments': HR_DEPARTMENTS_REFRESH_EVENT,
  '/hr/leaves': HR_LEAVES_REFRESH_EVENT,
  '/hr/accidents': HR_ACCIDENTS_REFRESH_EVENT,
  '/hr/contracts': HR_CONTRACTS_REFRESH_EVENT,
  '/hr/training': HR_TRAINING_REFRESH_EVENT,
  '/hr/evaluations': HR_EVALUATIONS_REFRESH_EVENT,
  '/employee-portal/documents': HR_PORTAL_DOCUMENTS_REFRESH_EVENT,
  '/employee-portal/service-requests': HR_PORTAL_SERVICE_REQUESTS_REFRESH_EVENT,
  '/employee-portal/announcements': HR_PORTAL_ANNOUNCEMENTS_REFRESH_EVENT,
  '/employee-portal/dashboard': HR_PORTAL_DASHBOARD_REFRESH_EVENT,
  '/employee-portal/profile': HR_EMPLOYEES_REFRESH_EVENT,
};

/** All HR entity events — portal pages and dashboards can listen once. */
export const ALL_HR_DATA_REFRESH_EVENTS = [
  HR_EMPLOYEES_REFRESH_EVENT,
  HR_DEPARTMENTS_REFRESH_EVENT,
  HR_LEAVES_REFRESH_EVENT,
  HR_ACCIDENTS_REFRESH_EVENT,
  HR_CONTRACTS_REFRESH_EVENT,
  HR_TRAINING_REFRESH_EVENT,
  HR_EVALUATIONS_REFRESH_EVENT,
  HR_PORTAL_DOCUMENTS_REFRESH_EVENT,
  HR_PORTAL_SERVICE_REQUESTS_REFRESH_EVENT,
  HR_PORTAL_ANNOUNCEMENTS_REFRESH_EVENT,
  HR_PORTAL_DASHBOARD_REFRESH_EVENT,
];

/** Strip `/api` prefix so callers may pass either `/api/hr/leaves` or `/hr/leaves`. */
export function normalizeHrApiPath(apiPath) {
  if (apiPath == null || apiPath === '') return '';
  let path = String(apiPath).trim();
  if (path.startsWith('/api/')) path = path.slice(4);
  return path.replace(/\/+$/, '') || path;
}

export function dispatchHrApiRefresh(apiPath) {
  const normalized = normalizeHrApiPath(apiPath);
  const eventName = HR_REFRESH_BY_API[normalized];
  if (eventName && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(eventName));
  }
}

/** Dispatch refresh for any portal/HR list path (with or without `/api`). */
export function dispatchHrRefreshForApiPath(apiPath) {
  dispatchHrApiRefresh(apiPath);
  const normalized = normalizeHrApiPath(apiPath);
  if (normalized.startsWith('/employee-portal/')) {
    dispatchHrApiRefresh('/employee-portal/dashboard');
  }
}

export function dispatchHrEmployeesRefresh() {
  dispatchHrApiRefresh('/hr/employees');
}

export function dispatchHrDepartmentsRefresh() {
  dispatchHrApiRefresh('/management/departments');
}

export function dispatchHrLeavesRefresh() {
  dispatchHrApiRefresh('/hr/leaves');
}

export function dispatchHrContractsRefresh() {
  dispatchHrApiRefresh('/hr/contracts');
}

export function dispatchHrTrainingRefresh() {
  dispatchHrApiRefresh('/hr/training');
}

export function dispatchHrEvaluationsRefresh() {
  dispatchHrApiRefresh('/hr/evaluations');
}

/** Subscribe to one or more HR refresh events (Products Variantes / CRM pattern). */
export function useHrApiRefreshListener(events, handler) {
  const list = Array.isArray(events) ? events : [events];
  const key = JSON.stringify(list);
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!list.length) return undefined;
    const wrapped = () => { handlerRef.current?.(); };
    list.forEach((ev) => window.addEventListener(ev, wrapped));
    return () => list.forEach((ev) => window.removeEventListener(ev, wrapped));
  }, [key]);
}
